import { IconAdCircle, IconBuildingFactory, IconClipboardCheck, IconDashboard, IconDatabase, IconDeviceDesktop, IconDeviceGamepad, IconFlagQuestion, IconNumber, IconPlane, IconReceipt, IconSettings, IconShoppingBag, IconShoppingCart, IconStethoscope, IconSticker, IconUserCog } from "@tabler/icons-react"
import { canViewLogistics } from "@shared/utils/logistics-permissions"
import { ElementType } from "react"
import { ROLES } from "@/shared"

export interface ILink {
  to?: string
  label: string
  icon: ElementType
  visible: boolean
  badge?: number
  /** Código de país (pe, ec): muestra su bandera junto al nombre, en el título del grupo y en cada opción. */
  flag?: string
  children?: Array<Omit<ILink, "icon">>
}

/**
 * Quién ve qué en Rendiciones de Gastos. Todo colaborador ve "Rendición de viáticos" en Perú y Ecuador; "Caja chica" y
 * "Tarjetas corporativas" solo si está en esa configuración del país. Quien es Administración o Contabilidad de un solo
 * país ve únicamente ese país (todo lo demás del módulo le queda oculto); administradores y los roles generales ven ambos.
 */
const rendicionVisible = (hasRole: (role: string) => boolean, accesos?: { cajaChica: Record<string, boolean>; tarjetas: Record<string, boolean> }, paisUsuario?: string) => {
  const admin = hasRole(ROLES.ADMIN)
  const gestor = admin || hasRole(ROLES.RENDICION_EDIT)
  const global = gestor || hasRole(ROLES.RENDICION_CONTABLE)
  const asignados = [
    ...(hasRole(ROLES.RENDICION_APPROVE_PE) || hasRole(ROLES.RENDICION_CONTABLE_PE) ? ["Perú"] : []),
    ...(hasRole(ROLES.RENDICION_APPROVE_EC) || hasRole(ROLES.RENDICION_CONTABLE_EC) ? ["Ecuador"] : [])
  ]
  const textoPais = (paisUsuario || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  const paisCuenta = textoPais.includes("ecuador") ? "Ecuador" : textoPais.includes("peru") ? "Perú" : undefined
  const pais = (aprueba: string, contable: string, nombre: string) => {
    // El colaborador (sin roles de gestión) ve solo el país asignado a su cuenta; sin país asignado ve ambos.
    const grupo = global || (asignados.length === 0 ? !paisCuenta || paisCuenta === nombre : asignados.includes(nombre))
    return {
      grupo,
      rinde: grupo,
      // Administración del país también entra a Caja chica y Tarjetas de ese país para configurarlas.
      cajaChica: grupo && (gestor || hasRole(aprueba) || !!accesos?.cajaChica?.[nombre]),
      tarjetas: grupo && (gestor || hasRole(aprueba) || !!accesos?.tarjetas?.[nombre]),
      revisa: gestor || hasRole(aprueba),
      contabilidad: admin || hasRole(ROLES.RENDICION_CONTABLE) || hasRole(contable),
      reporte: admin || hasRole(aprueba)
    }
  }
  return { ec: pais(ROLES.RENDICION_APPROVE_EC, ROLES.RENDICION_CONTABLE_EC, "Ecuador"), pe: pais(ROLES.RENDICION_APPROVE_PE, ROLES.RENDICION_CONTABLE_PE, "Perú") }
}

/** Roles de Compras Ecuador que habilitan cada pantalla (el back vuelve a validarlos y además exige país Ecuador). */
const EC = {
  ADMIN: [ROLES.PROCUREMENT_EC_ADMIN],
  REQUEST: [ROLES.PROCUREMENT_EC_ADMIN, ROLES.PROCUREMENT_EC_REQUEST_ADMIN, ROLES.PROCUREMENT_EC_REQUEST_CREATE, ROLES.PROCUREMENT_EC_REQUEST_VIEW_ALL],
  ORDER_VIEW: [ROLES.PROCUREMENT_EC_ADMIN, ROLES.PROCUREMENT_EC_REQUEST_ADMIN, ROLES.PROCUREMENT_EC_ORDER_CREATE, ROLES.PROCUREMENT_EC_ORDER_VIEW, ROLES.PROCUREMENT_EC_ORDER_ACCOUNTING],
  ACCOUNTING: [ROLES.PROCUREMENT_EC_ADMIN, ROLES.PROCUREMENT_EC_ORDER_ACCOUNTING],
  PROVIDER_VIEW: [ROLES.PROCUREMENT_EC_ADMIN, ROLES.PROCUREMENT_EC_PROVIDER_VIEW, ROLES.PROCUREMENT_EC_PROVIDER_CREATE, ROLES.PROCUREMENT_EC_PROVIDER_EDIT],
  ADVANCE: [ROLES.PROCUREMENT_EC_ADMIN, ROLES.PROCUREMENT_EC_ADVANCE_CREATE, ROLES.PROCUREMENT_EC_ADVANCE_VIEW_ALL, ROLES.PROCUREMENT_EC_ADVANCE_ACCOUNTING],
  REPORT: [ROLES.PROCUREMENT_EC_ADMIN, ROLES.PROCUREMENT_EC_REPORT_VIEW, ROLES.PROCUREMENT_EC_REQUEST_ADMIN, ROLES.PROCUREMENT_EC_REQUEST_VIEW_ALL]
}
// La búsqueda de proveedores en SAP la usan también quienes crean órdenes.
const EC_ANY = [...new Set(Object.values(EC).flat())]
const ecCan = (hasRole: (role: string) => boolean, roles: string[]) => hasRole(ROLES.ADMIN) || roles.some((r) => hasRole(r))

export const mainLinks = ({ hasRole, pendientesRendicion, accesosRendicion, paisUsuario }: { hasRole: (role: string) => boolean; pendientesRendicion?: Record<string, number>; accesosRendicion?: { cajaChica: Record<string, boolean>; tarjetas: Record<string, boolean> }; paisUsuario?: string }): Array<ILink> => [
  { to: "/", label: "Inicio", icon: IconDashboard, visible: true },
  {
    label: "DATA",
    icon: IconDatabase,
    visible: hasRole(ROLES.ADMIN) || ((hasRole(ROLES.DATA_VIEW) || hasRole(ROLES.TI_VIEW) || hasRole(ROLES.USER_VIEW) || hasRole(ROLES.USER) || hasRole(ROLES.TI_OWN_VIEW) || hasRole(ROLES.TICKET_DATA_ADMIN)) && !hasRole(ROLES.PROVIDER) && !hasRole(ROLES.EXTERNAL_PROVIDER)),
    children: [
      {
        to: "/data/tickets",
        label: "Tickets",
        visible: hasRole(ROLES.ADMIN) || ((hasRole(ROLES.USER_VIEW) || hasRole(ROLES.TI_VIEW) || hasRole(ROLES.USER) || hasRole(ROLES.DATA_VIEW) || hasRole(ROLES.TI_OWN_VIEW) || hasRole(ROLES.TICKET_DATA_ADMIN)) && !hasRole(ROLES.EXTERNAL_PROVIDER))
      },
      { to: "/data/dashboard", label: "Dashboard", visible: hasRole(ROLES.ADMIN) || ((hasRole(ROLES.TI_VIEW) || hasRole(ROLES.DATA_VIEW) || hasRole(ROLES.TICKET_DATA_ADMIN)) && !hasRole(ROLES.PROVIDER) && !hasRole(ROLES.EXTERNAL_PROVIDER)) }
    ]
  },
  {
    label: "TI",
    icon: IconDeviceDesktop,
    visible:
      hasRole(ROLES.USER_VIEW) || hasRole(ROLES.USER) || hasRole(ROLES.TI_VIEW) || hasRole(ROLES.ADMIN) || hasRole(ROLES.DATA_VIEW) || hasRole(ROLES.PROJECT_CREATOR) || hasRole(ROLES.TI_OWN_VIEW) || hasRole(ROLES.TICKET_DATA_ADMIN) || hasRole(ROLES.EXTERNAL_PROVIDER) || hasRole(ROLES.PROVIDER),
    children: [
      {
        to: "/ti/tickets",
        label: "Tickets",
        visible: hasRole(ROLES.USER_VIEW) || hasRole(ROLES.USER) || hasRole(ROLES.TI_VIEW) || hasRole(ROLES.ADMIN) || hasRole(ROLES.DATA_VIEW) || hasRole(ROLES.TI_OWN_VIEW) || hasRole(ROLES.TICKET_DATA_ADMIN) || hasRole(ROLES.EXTERNAL_PROVIDER) || hasRole(ROLES.PROVIDER)
      },
      { to: "/ti/projects", label: "Proyectos", visible: hasRole(ROLES.PROJECT_CREATOR) || hasRole(ROLES.ADMIN) },
      { to: "/ti/dashboards", label: "KPIs", visible: hasRole(ROLES.TI_VIEW) || hasRole(ROLES.ADMIN) || hasRole(ROLES.TICKET_DATA_ADMIN) }
    ]
  },
  {
    label: "Activos",
    icon: IconDeviceGamepad,
    visible: hasRole(ROLES.ASSETS_VIEW) || hasRole(ROLES.T_ASSET_COMMENT) || hasRole(ROLES.ADMIN),
    children: [{ to: "/assets/tickets", label: "Tickets", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ASSETS_VIEW) || hasRole(ROLES.T_ASSET_COMMENT) }]
  },
  {
    label: "Marketing",
    icon: IconAdCircle,
    visible: hasRole(ROLES.MARKETING_VIEW) || hasRole(ROLES.MARKETING_READ) || hasRole(ROLES.T_MKT_COMMENT) || hasRole(ROLES.ADMIN),
    children: [
      {
        to: "/mkt/tickets",
        label: "Tickets",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.MARKETING_VIEW) || hasRole(ROLES.MARKETING_READ) || hasRole(ROLES.T_MKT_COMMENT)
      }
    ]
  },
  {
    label: "Contabilidad",
    icon: IconNumber,
    visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ACCOUNTING_PERU_VIEW) || hasRole(ROLES.ACCOUNTING_ECUADOR_VIEW),
    children: [
      { to: "/accounting/tickets-peru", label: "PE - Tickets", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ACCOUNTING_PERU_VIEW) },
      { to: "/accounting/tickets-ecuador", label: "EC - Tickets", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ACCOUNTING_ECUADOR_VIEW) }
    ]
  },
  {
    label: "Stickers",
    icon: IconSticker,
    visible: hasRole(ROLES.PRINT_STICKER) || hasRole(ROLES.ADMIN),
    to: "/stickers"
  },
  {
    label: "Administración",
    icon: IconUserCog,
    visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PERSONAL_VIEW) || hasRole(ROLES.GESTION_PERSONAL),
    children: [{ to: "/admin/personal", label: "Personal", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PERSONAL_VIEW) || hasRole(ROLES.GESTION_PERSONAL) }]
  },
  {
    label: "Compliance",
    icon: IconClipboardCheck,
    visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.AYUDANTIA_VIEW) || hasRole(ROLES.AYUDANTIA_CREATE) || hasRole(ROLES.AYUDANTIA_APPROVE) || hasRole(ROLES.COMPLIANCE_VIEW) || hasRole(ROLES.COMPLIANCE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_ECUADOR_VIEW),
    children: [
      { to: "/compliance/ayudantias", label: "Formación clínica · Ayudantía", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.AYUDANTIA_VIEW) || hasRole(ROLES.AYUDANTIA_CREATE) || hasRole(ROLES.AYUDANTIA_APPROVE) || hasRole(ROLES.COMPLIANCE_VIEW) || hasRole(ROLES.COMPLIANCE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_ECUADOR_VIEW) },
      { to: "/compliance/medicos", label: "Base de médicos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.AYUDANTIA_VIEW) || hasRole(ROLES.AYUDANTIA_CREATE) || hasRole(ROLES.AYUDANTIA_APPROVE) || hasRole(ROLES.COMPLIANCE_VIEW) || hasRole(ROLES.COMPLIANCE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_ECUADOR_VIEW) },
      { to: "/compliance/plantillas-contrato", label: "Plantillas de contrato", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.AYUDANTIA_VIEW) || hasRole(ROLES.AYUDANTIA_CREATE) || hasRole(ROLES.AYUDANTIA_APPROVE) || hasRole(ROLES.COMPLIANCE_VIEW) || hasRole(ROLES.COMPLIANCE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_ECUADOR_VIEW) }
    ]
  },
  {
    label: "RedFlags",
    icon: IconFlagQuestion,
    visible:
      hasRole(ROLES.ADMIN) ||
      hasRole(ROLES.COMPLIANCE_VIEW) ||
      hasRole(ROLES.COMPLIANCE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_PERU_CREATE) || hasRole(ROLES.COMPLIANCE_PERU_EDIT) ||
      hasRole(ROLES.COMPLIANCE_ECUADOR_VIEW) || hasRole(ROLES.COMPLIANCE_ECUADOR_CREATE) || hasRole(ROLES.COMPLIANCE_ECUADOR_EDIT) ||
      hasRole(ROLES.COMPLIANCE_EXPENSE_VIEW) ||
      hasRole(ROLES.COMPLIANCE_EXPENSE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_EXPENSE_PERU_CREATE) || hasRole(ROLES.COMPLIANCE_EXPENSE_PERU_EDIT) ||
      hasRole(ROLES.COMPLIANCE_EXPENSE_ECUADOR_VIEW) || hasRole(ROLES.COMPLIANCE_EXPENSE_ECUADOR_CREATE) || hasRole(ROLES.COMPLIANCE_EXPENSE_ECUADOR_EDIT) ||
      hasRole(ROLES.COMPLIANCE_QUALITY_VIEW),
    children: [
      {
        to: "/compliance/red-flags/compliance-peru",
        label: "Compliance - Perú",
        visible:
          hasRole(ROLES.ADMIN) ||
          hasRole(ROLES.COMPLIANCE_VIEW) ||
          hasRole(ROLES.COMPLIANCE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_PERU_CREATE) || hasRole(ROLES.COMPLIANCE_PERU_EDIT)
      },
      {
        to: "/compliance/red-flags/compliance-ecuador",
        label: "Compliance - Ecuador",
        visible:
          hasRole(ROLES.ADMIN) ||
          hasRole(ROLES.COMPLIANCE_VIEW) ||
          hasRole(ROLES.COMPLIANCE_ECUADOR_VIEW) || hasRole(ROLES.COMPLIANCE_ECUADOR_CREATE) || hasRole(ROLES.COMPLIANCE_ECUADOR_EDIT)
      },
      { to: "/quality/red-flags", label: "Calidad", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.COMPLIANCE_QUALITY_VIEW) },
      {
        to: "/compliance/red-flags/rendicion-gasto-peru",
        label: "Rendición de Gastos - Perú",
        visible:
          hasRole(ROLES.ADMIN) ||
          hasRole(ROLES.COMPLIANCE_EXPENSE_VIEW) ||
          hasRole(ROLES.COMPLIANCE_EXPENSE_PERU_VIEW) || hasRole(ROLES.COMPLIANCE_EXPENSE_PERU_CREATE) || hasRole(ROLES.COMPLIANCE_EXPENSE_PERU_EDIT)
      },
      {
        to: "/compliance/red-flags/rendicion-gasto-ecuador",
        label: "Rendición de Gastos - Ecuador",
        visible:
          hasRole(ROLES.ADMIN) ||
          hasRole(ROLES.COMPLIANCE_EXPENSE_VIEW) ||
          hasRole(ROLES.COMPLIANCE_EXPENSE_ECUADOR_VIEW) || hasRole(ROLES.COMPLIANCE_EXPENSE_ECUADOR_CREATE) || hasRole(ROLES.COMPLIANCE_EXPENSE_ECUADOR_EDIT)
      }
    ]
  },
  {
    label: "Inventario",
    icon: IconBuildingFactory,
    visible:
      hasRole(ROLES.ADMIN) ||
      hasRole(ROLES.INVENTORY_PERU_VIEW) ||
      hasRole(ROLES.INVENTORY_ECUADOR_VIEW) ||
      hasRole(ROLES.BANDEJAS_ACT_FIJOS_ESP) ||
      hasRole(ROLES.BANDEJAS_ACT_FIJOS_VIEW) ||
      hasRole(ROLES.BANDEJAS_ACT_FIJOS_CREATE) ||
      hasRole(ROLES.BANDEJAS_ACT_FIJOS_EDIT) ||
      hasRole(ROLES.BANDEJAS_ACT_FIJOS_DELETE) ||
      hasRole(ROLES.INV_SPECIALIZED_VIEW) ||
      hasRole(ROLES.INV_SPECIALIZED_CREATE) ||
      hasRole(ROLES.INV_SPECIALIZED_EDIT) ||
      hasRole(ROLES.INV_SPECIALIZED_DELETE) ||
      hasRole(ROLES.PRODUCT) ||
      hasRole(ROLES.TRAY_GUIDE) ||
      hasRole(ROLES.INVENTORY_PROVIDER_ADMIN),
    children: [
      { to: "/inventory/product", label: "Productos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PRODUCT) },
      { to: "/inventory/non-specialized-peru", label: "No especializado - Perú", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.INVENTORY_PERU_VIEW) },
      { to: "/inventory/non-specialized-ecuador", label: "No especializado - Ecuador", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.INVENTORY_ECUADOR_VIEW) },
      {
        label: "Activos",
        visible:
          hasRole(ROLES.ADMIN) ||
          hasRole(ROLES.INV_SPECIALIZED) ||
          hasRole(ROLES.INV_SPECIALIZED_VIEW) ||
          hasRole(ROLES.INV_SPECIALIZED_CREATE) ||
          hasRole(ROLES.INV_SPECIALIZED_EDIT) ||
          hasRole(ROLES.INV_SPECIALIZED_DELETE) ||
          hasRole(ROLES.BANDEJAS_ACT_FIJOS_ESP) ||
          hasRole(ROLES.BANDEJAS_ACT_FIJOS_VIEW) ||
          hasRole(ROLES.BANDEJAS_ACT_FIJOS_CREATE) ||
          hasRole(ROLES.BANDEJAS_ACT_FIJOS_EDIT) ||
          hasRole(ROLES.BANDEJAS_ACT_FIJOS_DELETE) ||
          hasRole(ROLES.TRAY_GUIDE) ||
          hasRole(ROLES.LAVADO_INSTRUMENTAL_CREATE) ||
          hasRole(ROLES.LAVADO_INSTRUMENTAL_VIEW),
        children: [
          { to: "/inventory/guide-tray", label: "Guía - Bandejas", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAY_GUIDE) },
          { to: "/inventory/guides", label: "Historial de Guías", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAY_GUIDE) },
          { to: "/inventory/manual-guides", label: "Guías manuales", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAY_GUIDE) },
          {
            to: "/inventory/specialized",
            label: "Especializados",
            visible:
              hasRole(ROLES.ADMIN) ||
              hasRole(ROLES.INV_SPECIALIZED) ||
              hasRole(ROLES.INV_SPECIALIZED_VIEW) ||
              hasRole(ROLES.INV_SPECIALIZED_CREATE) ||
              hasRole(ROLES.INV_SPECIALIZED_EDIT) ||
              hasRole(ROLES.INV_SPECIALIZED_DELETE)
          },
          {
            to: "/inventory/bandejas-act-fijos-especializados",
            label: "Bandejas",
            visible:
              hasRole(ROLES.ADMIN) ||
              hasRole(ROLES.BANDEJAS_ACT_FIJOS_ESP) ||
              hasRole(ROLES.BANDEJAS_ACT_FIJOS_VIEW) ||
              hasRole(ROLES.BANDEJAS_ACT_FIJOS_CREATE) ||
              hasRole(ROLES.BANDEJAS_ACT_FIJOS_EDIT) ||
              hasRole(ROLES.BANDEJAS_ACT_FIJOS_DELETE)
          },
          {
            to: "/inventory/lavado-instrumental",
            label: "Lavado de Instrumental",
            visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.LAVADO_INSTRUMENTAL_CREATE) || hasRole(ROLES.LAVADO_INSTRUMENTAL_VIEW)
          }
        ]
      },
      { to: "/inventory/provider", label: "Proveedores", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.INVENTORY_PROVIDER_ADMIN) },
      {
        label: "Logística",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAY_GUIDE),
        children: [
          { to: "/inventory/logistica/reportes", label: "Reportes", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAY_GUIDE) }
        ]
      }
    ]
  },
  {
    label: "Compras",
    icon: IconShoppingCart,
    // Oculto a pedido de Michelle (2026-10-05): se está migrando a "Sistema de Compras"
    // (/procurement/...). Los datos y el backend siguen intactos, solo se quita del menú.
    visible: false,
    children: [
      { to: "/compras/requerimientos", label: "Requerimientos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PURCHASE_REQUIREMENT_VIEW) },
      {
        to: "/compras/aprobaciones-pendientes",
        label: "Aprobaciones",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PURCHASE_APPROVAL_APPROVE) || hasRole(ROLES.PURCHASE_APPROVAL_REJECT)
      },
      { to: "/compras/ordenes", label: "Órdenes", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PURCHASE_ORDER_VIEW) },
      {
        to: "/compras/anticipos",
        label: "Anticipos",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ANTICIPO_ADMIN) || hasRole(ROLES.ANTICIPO_USER) || hasRole(ROLES.ANTICIPO_APROBADOR)
      },
      { to: "/compras/proveedores", label: "Proveedores", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PURCHASE_PROVIDER_VIEW) },
      { to: "/compras/facturas", label: "Facturas", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PURCHASE_INVOICE_VIEW) }
    ]
  },
  {
    label: "Sistema de Compras",
    icon: IconShoppingCart,
    visible:
      hasRole(ROLES.ADMIN) ||
      hasRole(ROLES.PROCUREMENT_REQUEST_CREATE) ||
      hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) ||
      hasRole(ROLES.PROCUREMENT_REQUEST_VIEW_ALL) ||
      hasRole(ROLES.PROCUREMENT_ORDER_CREATE) ||
      hasRole(ROLES.PROCUREMENT_ORDER_VIEW) ||
      hasRole(ROLES.PROCUREMENT_PROVIDER_VIEW) ||
      hasRole(ROLES.PROCUREMENT_PROVIDER_CREATE) ||
      hasRole(ROLES.PROCUREMENT_ADVANCE_CREATE) ||
      hasRole(ROLES.PROCUREMENT_ADVANCE_VIEW_ALL),
    children: [
      {
        to: "/procurement/solicitudes",
        label: "Solicitudes de compra",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_CREATE) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_VIEW_ALL)
      },
      {
        to: "/procurement/aprobaciones",
        label: "Aprobaciones",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_CREATE)
      },
      {
        to: "/procurement/ordenes",
        label: "Órdenes de compra",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_ORDER_CREATE) || hasRole(ROLES.PROCUREMENT_ORDER_VIEW)
      },
      {
        to: "/procurement/proveedores",
        label: "Proveedores",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_PROVIDER_VIEW) || hasRole(ROLES.PROCUREMENT_PROVIDER_CREATE)
      },
      {
        to: "/procurement/productos",
        label: "Productos",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_CATALOG_VIEW) || hasRole(ROLES.PROCUREMENT_CATALOG_CREATE)
      },
      {
        to: "/procurement/plantillas-mensuales",
        label: "Plantillas mensuales",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_CREATE)
      },
      {
        to: "/procurement/anticipos",
        label: "Anticipos",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_ADVANCE_CREATE) || hasRole(ROLES.PROCUREMENT_ADVANCE_VIEW_ALL)
      }
    ]
  },
  {
    // Migración de Compras Ecuador (Flask → Suite). Etapa de lectura de todos los módulos; las escrituras llegan por etapas. Independiente del "Sistema de Compras" de Perú.
    // Cada ítem usa los mismos roles que el endpoint del back (que además exige país Ecuador). "Aprobaciones" es para cualquier usuario de Ecuador: cada quien ve solo lo asignado a él.
    label: "Compras Ecuador",
    icon: IconShoppingBag,
    visible: ecCan(hasRole, EC_ANY),
    children: [
      { to: "/procurement-ec/solicitudes", label: "Solicitudes de compra", visible: ecCan(hasRole, EC.REQUEST) },
      { to: "/procurement-ec/approvals", label: "Aprobaciones", visible: ecCan(hasRole, EC_ANY) },
      { to: "/procurement-ec/pre-orders", label: "Pre-órdenes", visible: false /* oculta: Ecuador pasa a cotizaciones por solicitud, como Perú */ },
      { to: "/procurement-ec/orders", label: "Órdenes de compra", visible: ecCan(hasRole, EC.ORDER_VIEW) },
      { to: "/procurement-ec/sap-invoices", label: "Facturas SAP", visible: ecCan(hasRole, EC.ACCOUNTING) },
      { to: "/procurement-ec/repositorio-facturas", label: "Repositorio de facturas", visible: ecCan(hasRole, EC.ORDER_VIEW) },
      { to: "/procurement-ec/advances", label: "Anticipos", visible: ecCan(hasRole, EC.ADVANCE) },
      { to: "/procurement-ec/suppliers", label: "Proveedores", visible: ecCan(hasRole, EC.PROVIDER_VIEW) },
      { to: "/procurement-ec/productos", label: "Productos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.PROCUREMENT_REQUEST_ADMIN) || hasRole(ROLES.PROCUREMENT_CATALOG_VIEW) || hasRole(ROLES.PROCUREMENT_CATALOG_CREATE) },
      { to: "/procurement-ec/monthly", label: "Plantillas mensuales", visible: ecCan(hasRole, EC.REQUEST) },
      { to: "/procurement-ec/reports", label: "Reportes", visible: ecCan(hasRole, EC.REPORT) }
    ]
  },
  {
    label: "Rendiciones de Gastos",
    icon: IconReceipt,
    visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.grupo || rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.grupo,
    children: [
      {
        label: "Ecuador",
        flag: "ec",
        visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.grupo,
        children: [
          { to: "/rendiciones/viajes-ecuador", label: "Rendición de viáticos", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.rinde },
          { to: "/rendiciones/caja-chica-ecuador", label: "Caja chica", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.cajaChica },
          { to: "/rendiciones/tarjetas-ecuador", label: "Tarjetas corporativas", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.tarjetas },
          { to: "/rendiciones/revision-ecuador", label: "Revisión de Rendiciones", badge: pendientesRendicion?.["Ecuador"], visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.revisa },
          { to: "/rendiciones/contabilidad-ecuador", label: "Rendiciones aprobadas", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.contabilidad },
          { to: "/rendiciones/reporteria-ecuador", label: "Reportería", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).ec.reporte }
        ]
      },
      {
        label: "Perú",
        flag: "pe",
        visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.grupo,
        children: [
          { to: "/rendiciones/viajes-peru", label: "Rendición de viáticos", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.rinde },
          { to: "/rendiciones/caja-chica-peru", label: "Caja chica", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.cajaChica },
          { to: "/rendiciones/tarjetas-peru", label: "Tarjetas corporativas", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.tarjetas },
          { to: "/rendiciones/revision-peru", label: "Revisión de Rendiciones", badge: pendientesRendicion?.["Perú"], visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.revisa },
          { to: "/rendiciones/contabilidad-peru", label: "Rendiciones aprobadas", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.contabilidad },
          { to: "/rendiciones/reporteria-peru", label: "Reportería", visible: rendicionVisible(hasRole, accesosRendicion, paisUsuario).pe.reporte }
        ]
      }
    ]
  },
  {
    label: "Viajes",
    icon: IconPlane,
    visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAVEL_REQUEST_VIEW) || hasRole(ROLES.TRAVEL_REQUEST_CREATE) || (!hasRole(ROLES.PROVIDER) && !hasRole(ROLES.EXTERNAL_PROVIDER)),
    children: [
      { to: "/viajes/solicitudes", label: "Solicitudes de viaje", visible: true },
      { to: "/viajes/mis-solicitudes", label: "Mis solicitudes", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAVEL_REQUEST_VIEW) || hasRole(ROLES.TRAVEL_REQUEST_ADMIN) },
      { to: "/viajes/proveedores", label: "Aerolíneas, hoteles y transporte", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAVEL_REQUEST_VIEW) || hasRole(ROLES.TRAVEL_REQUEST_ADMIN) },
      { to: "/viajes/jefes-area", label: "Jefes de área", visible: hasRole(ROLES.ADMIN) },
      { to: "/viajes/reporteria", label: "Reportería", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.TRAVEL_REQUEST_VIEW) || hasRole(ROLES.TRAVEL_REQUEST_ADMIN) || hasRole(ROLES.ADDITIONAL_REQUEST_VIEW_EDUCACION_MEDICA) }
    ]
  },
  {
    label: "Gestión de la App",
    icon: IconStethoscope,
    visible:
      hasRole(ROLES.ADMIN) ||
      hasRole(ROLES.EDUCATION_VIEW) ||
      hasRole(ROLES.DOCTOR_VIEW) ||
      hasRole(ROLES.CLINIC_VIEW) ||
      hasRole(ROLES.VISIT_VIEW) ||
      hasRole(ROLES.COMERCIAL_VIEW) ||
      hasRole(ROLES.COMERCIAL_CREATE) ||
      hasRole(ROLES.DATA_IMPORT) ||
      canViewLogistics(hasRole),
    children: [
      {
        label: "Comercial",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.COMERCIAL_VIEW) || hasRole(ROLES.COMERCIAL_CREATE) || hasRole(ROLES.DATA_IMPORT),
        children: [
          {
            to: "/medical-suite/comercial/programacion",
            label: "Programación Mensual",
            visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.COMERCIAL_VIEW) || hasRole(ROLES.COMERCIAL_CREATE) || hasRole(ROLES.DATA_IMPORT)
          }
        ]
      },
      {
        label: "Educación Médica",
        visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_VIEW) || hasRole(ROLES.DOCTOR_VIEW) || hasRole(ROLES.CLINIC_VIEW) || hasRole(ROLES.VISIT_VIEW),
        children: [
          { to: "/medical-suite/doctors", label: "Médicos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.DOCTOR_VIEW) || hasRole(ROLES.EDUCATION_VIEW) },
          { to: "/medical-suite/clinics", label: "Clínicas", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.CLINIC_VIEW) || hasRole(ROLES.EDUCATION_VIEW) },
          { to: "/medical-suite/events", label: "Eventos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_VIEW) },
          { to: "/medical-suite/visits", label: "Visitas", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.VISIT_VIEW) || hasRole(ROLES.EDUCATION_VIEW) },
          { to: "/medical-suite/workshops", label: "Talleres", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.EDUCATION_VIEW) }
        ]
      },
      {
        label: "Logística",
        visible: canViewLogistics(hasRole),
        children: [
          { to: "/medical-suite/logistica/motorizados", label: "Repartidores", visible: canViewLogistics(hasRole) },
          { to: "/medical-suite/logistica/programacion", label: "Entrega y Recojo de Productos", visible: canViewLogistics(hasRole) },
          { to: "/medical-suite/logistica/programacion-diaria", label: "Programación diaria", visible: canViewLogistics(hasRole) },
          { to: "/medical-suite/logistica/productos-pendientes", label: "Productos Pendientes de Asignar", visible: canViewLogistics(hasRole) },
          { to: "/medical-suite/logistica/mapa-en-vivo", label: "Mapa en Vivo", visible: canViewLogistics(hasRole) }
        ]
      }
    ]
  },
  {
    label: "Configuración",
    icon: IconSettings,
    visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ROLES_VIEW),
    children: [
      { to: "/config/users", label: "Usuarios", visible: hasRole(ROLES.ADMIN) },
      { to: "/config/roles-catalog", label: "Roles y Permisos", visible: hasRole(ROLES.ADMIN) || hasRole(ROLES.ROLES_VIEW) }
    ]
  }
]
